import { create } from "zustand";
import { Brand, BrandsQuery, BrandsState } from "../types/brand";
import { useFetcher } from "./fetcher";
import { Paging } from "../types/paging";
import { Metadata } from "../types/metadata";
import { useWorkspacesStore } from "./workspaces";

const makeBrands = (data?: object): Brand[] | undefined => {
  if (!data) return undefined;
  const items = Object.entries(data ?? {}).map(([key, value]) => {
    return {
      id: key,
      brand_name: value?.brand_name,
      sub_brands: makeBrands(value?.sub_brands),
    };
  });
  items.sort((a: Brand, b: Brand) => a.id.localeCompare(b.id));
  return items;
};

export const useBrandsStore = create<BrandsState>(
  (set, get) =>
    ({
      all: null,
      items: [],
      total: null,
      query: null,
      search: async (query?: BrandsQuery, fetch?: boolean) => {
        if (fetch) {
          const page = await get().filter(query);
          set(() => ({
            query,
            items: page?.items ?? [],
            total: page?.items.length ?? 0,
          }));
        } else {
          set(() => ({ query }));
        }
      },
      filter: async (
        query?: BrandsQuery | null
      ): Promise<Paging<Brand> | null> => {
        let all = get().all;
        if (all === null) {
          await get().fetch(false);
          all = get().all ?? [];
        }

        let items: Brand[] = [];
        if (!query || (!query.brand_name && !query.id && !query.workspace_id)) {
          items = all;
        } else {
          const workspace = query.workspace_id
            ? await useWorkspacesStore.getState().get(query.workspace_id)
            : null;
          items = all.filter((brand: Brand) => {
            if (query.id && (!brand.id || !brand.id.startsWith(query.id))) {
              return false;
            }
            if (
              query.brand_name &&
              (!brand.brand_name ||
                !brand.brand_name.startsWith(query.brand_name))
            ) {
              return false;
            }
            if (
              workspace &&
              workspace.group_brands &&
              !workspace.group_brands.includes(brand.id)
            ) {
              return false;
            }
            return true;
          });
        }

        if (query?.order_by) {
          items.sort((a: Brand, b: Brand) => {
            for (const [key, value] of Object.entries(query.order_by!)) {
              const va = ((a as any)[key] as string | undefined) ?? "";
              const vb = ((b as any)[key] as string | undefined) ?? "";
              if (value === "desc") {
                const v = vb.localeCompare(va);
                if (v === 0) {
                  continue;
                }
                return v;
              } else {
                const v = va.localeCompare(vb);
                if (v === 0) {
                  continue;
                }
                return v;
              }
            }
            return 0;
          });
        }
        return { items, total: items.length };
      },
      fetch: async (reset: boolean): Promise<Paging<Brand> | null> => {
        try {
          const { all, query, filter } = get();
          if (!reset && all !== null) {
            return null;
          }

          try {
            const res = await useFetcher
              .getState()
              .omsDsFetcher.get<Metadata>(`metadata/brand_configuration`);
            const brands = makeBrands(res?.data?.data);
            set(() => ({
              all: brands ?? [],
            }));
            const page = await filter(query);
            set(() => ({
              items: page?.items ?? [],
              total: page?.total ?? 0,
            }));
            return page;
          } catch (error) {
            throw error;
          }
        } catch (error) {
          throw error;
        }
      },
      get: async (id: string): Promise<Brand | undefined> => {
        let all = get().all;
        if (all === null) {
          await get().fetch(false);
          all = get().all ?? [];
        }
        return all.find((v: Brand) => v.id === id);
      },
      getSubBrands: async (brandId: string | string[]): Promise<Brand[] | undefined> => {
        let all = get().all;
        if (all === null) {
          await get().fetch(false);
          all = get().all ?? [];
        }
        const isArray = Array.isArray(brandId);
        return all.reduce<Brand[]>((prev: Brand[], cur: Brand) => {
          if (cur.sub_brands && cur.sub_brands.length > 0 && ((isArray && brandId.includes(cur.id)) || brandId === cur.id)) {
            return [...prev, ...cur.sub_brands];
          }
          return prev;
        }, [] as Brand[]);
      },
      clear: () => {
        set(() => ({ items: [], total: null }));
      },
    } as BrandsState)
);
